import type {
  AcademicTitle,
  BankTransactionStatus,
  CalculationBlockRole,
  CalculationBlockType,
  CatalogueKind,
  ConfiguratorAvailabilityConflict,
  ConfiguratorGroupKind,
  ConfiguratorImageRuleMode,
  ConfiguratorPriceMode,
  ConfiguratorSelectionType,
  ConfiguratorStatus,
  ContractDirection,
  ContractKind,
  ContractStatus,
  DeductionBasis,
  DeductionSettlement,
  EnergyLabel,
  FloorType,
  FormulaSummaryScope,
  HouseElementMode,
  HouseElementScope,
  InstallmentDueKind,
  InvoiceStatus,
  McpUploadTarget,
  PhoneType,
  PositionGate,
  PositionOrphanReason,
  PositionPriceAdjustment,
  ProductClassificationKind,
  ProductPropertyType,
  ProductSuggestionStatus,
  ProjectStatus,
  PropertyFeature,
  PropertyMetric,
  PropertyType,
  RoomType,
  Salutation,
  ScopeLevel,
  SubType,
  UserRole,
  UserType,
} from '../../enums'
import type { InstallmentState } from '../../models/finance/finance'

import { COMMON } from './common'
import { CONFIGURATOR_CMS } from './configurator-cms'
import { COST_LABELS } from './costs'
import { FIELD_LABELS } from './fields'
import { FINANCE_FIGURES, FINANCE_TERMS } from './finance'
import { TERMS } from './terms'

/** Property-wide rather than per room. Both scope enums say it. */
const SCOPE_GLOBAL = 'Global'

/** Where an answer lives — and with it, how often the question is asked. */
export const SCOPE_LEVEL_LABELS = {
  OBJECT: TERMS.property,
  FLOOR: TERMS.floor,
  ROOM: TERMS.room,
} as const satisfies Record<ScopeLevel, string>

/** Where the positions a question produces are booked — derived from the
 *  scopes it is asked at, never stored. */
export const POSITION_SCOPE_LABELS = {
  OBJECT: `Am ${TERMS.property}`,
  ROOM: `Am ${TERMS.room}`,
} as const

export const ROOM_TYPE_LABELS = {
  KITCHEN: 'Küche',
  BATHROOM_FULL: 'Vollbad',
  BATHROOM_SHOWER: 'Duschbad',
  GUEST_TOILET: 'Gäste-WC',
  LIVING_ROOM: 'Wohnzimmer',
  DINING_ROOM: 'Esszimmer',
  BEDROOM: 'Schlafzimmer',
  CHILDREN_ROOM: 'Kinderzimmer',
  OFFICE: 'Arbeitszimmer',
  ENTRANCE: 'Eingangsbereich',
  HALLWAY: 'Flur',
  STAIRCASE: 'Treppenhaus',
  STORAGE: TERMS.storageRoom,
  LAUNDRY: 'Hauswirtschaftsraum',
  WELLNESS_AREA: 'Wellnessbereich',
  OTHER: 'Sonstiger Raum',
} as const satisfies Record<RoomType, string>

/** A storey by the type it is stored as. Key order is the order every storey
 *  picker offers, top of the house down — `FLOOR_TYPE_VALUES` derives from
 *  it. */
export const FLOOR_TYPE_LABELS = {
  ATTIC: 'Dachgeschoss',
  FULL_FLOOR: TERMS.fullFloor,
  BASEMENT: 'Untergeschoss',
} as const satisfies Record<FloorType, string>

/**
 * Which storeys or rooms a rung reaches, as the words a reader sees.
 *
 * Empty for a plain group, which opens no scope and therefore reaches nothing
 * of its own. One list, so the badges beside a tree row and the line in a
 * compact reference cannot name different rooms.
 */
export function groupCoverage(group: {
  kind: ConfiguratorGroupKind
  roomTypes: readonly RoomType[]
  floorTypes: readonly FloorType[]
}): string[] {
  if (group.kind === 'GROUP') return []
  return group.kind === 'FLOOR'
    ? group.floorTypes.map(type => FLOOR_TYPE_LABELS[type])
    : group.roomTypes.map(type => ROOM_TYPE_LABELS[type])
}

/** The same storey types in the plural, for labels that count them. */
export const FLOOR_TYPE_PLURAL_LABELS = {
  ATTIC: 'Dachgeschosse',
  FULL_FLOOR: `${TERMS.fullFloor}e`,
  BASEMENT: 'Untergeschosse',
} as const satisfies Record<FloorType, string>

/**
 * A storey by where it sits in the stack. `ground` and `upper` are both
 * `FULL_FLOOR` rows — only the ordinal tells them apart, so these are not
 * enum labels and were never expressible as such.
 */
export const FLOOR_POSITION_LABELS = {
  GROUND: 'Erdgeschoss',
  UPPER: 'Obergeschoss',
} as const

export const PROPERTY_TYPE_LABELS = {
  HOUSE: 'Haus',
  APARTMENT: 'Wohnung',
} as const satisfies Record<PropertyType, string>

/** Equipment a property either has or does not — the Ausstattung checklist. */
export const PROPERTY_FEATURE_LABELS = {
  STORAGE_UNIT: TERMS.storageRoom,
  BASEMENT: 'Keller',
  FIREPLACE: 'Kamin',
  ELEVATOR: 'Aufzug',
  AIR_CONDITIONING: 'Klimaanlage',
  UNDERFLOOR_HEATING: 'Fußbodenheizung',
  SMART_HOME_SYSTEM: 'Smart Home',
  OPEN_KITCHEN: 'Offene Küche',
  SAUNA: 'Sauna',
  PRIVATE_ENTRANCE: 'Eigener Eingang',
  BALCONY: 'Balkon',
  GARDEN: 'Garten',
  TERRACE: 'Terrasse',
  PARKING_SPACE: 'Stellplatz',
} as const satisfies Record<PropertyFeature, string>

export const PROPERTY_SUB_TYPE_LABELS = {
  APARTMENT_NORMAL: 'Etagenwohnung',
  APARTMENT_MAISONETTE: 'Maisonette',
  APARTMENT_ATTIC: 'Dachgeschosswohnung',
  APARTMENT_PENTHOUSE: 'Penthouse',
  APARTMENT_TERRACED: 'Terrassenwohnung',
  APARTMENT_STUDIO: 'Studio',
  HOUSE_DETACHED: 'Einfamilienhaus',
  HOUSE_SEMI_DETACHED: 'Doppelhaushälfte',
  HOUSE_ROW_CORNER: 'Reihenendhaus',
  HOUSE_ROW_MIDDLE: 'Reihenmittelhaus',
  HOUSE_FARM: 'Hof',
  HOUSE_MULTI_FAMILY: 'Mehrfamilienhaus',
} as const satisfies Record<SubType, string>

/** Staff roles. `SALES` is the advising sales role, not after-sales support. */
export const USER_ROLE_LABELS = {
  CUSTOMER: TERMS.customer,
  ARCHITECT: 'Architekt',
  SALES: TERMS.advisor,
  PROJECT_MANAGER: TERMS.projectManager,
  SITE_MANAGER: TERMS.siteManager,
  ADMIN: 'Administrator',
  ROOT: 'Root',
} as const satisfies Record<UserRole, string>

/** Whether the account stands for a person or for an organisation. */
export const USER_TYPE_LABELS = {
  PERSON: 'Person',
  ORGANISATION: 'Organisation',
} as const satisfies Record<UserType, string>

/** How a person is addressed where the first name is not known. */
export const SALUTATION_LABELS = {
  HERR: 'Herr',
  FRAU: 'Frau',
  FAMILIE: 'Familie',
  EHELEUTE: 'Eheleute',
} as const satisfies Record<Salutation, string>

/** Written in front of the name, with the points the title is written with. */
export const ACADEMIC_TITLE_LABELS = {
  DR: 'Dr.',
  PROF: 'Prof.',
  PROF_DR: 'Prof. Dr.',
} as const satisfies Record<AcademicTitle, string>

/** What a stored phone number is for. */
export const PHONE_TYPE_LABELS = {
  ARBEIT: 'Arbeit',
  PRIVAT: 'Privat',
  MOBIL: 'Mobil',
  SONSTIGE: 'Sonstige',
} as const satisfies Record<PhoneType, string>

/** Whether a configurator is staff-only, open to the customer, or frozen. */
export const CONFIGURATOR_STATUS_LABELS = {
  INTERNAL: 'Intern',
  // Distinct from a released document — this is the configurator being
  // unlocked for the customer.
  OPEN: 'Offen',
  LOCKED: 'Gesperrt',
} as const satisfies Record<ConfiguratorStatus, string>

/** How a question lets the customer pick. Control names stay English, as IT
 *  vocabulary does throughout. */
export const SELECTION_TYPE_LABELS = {
  RADIO: 'Radio',
  CHECKBOX: 'Checkbox',
} as const satisfies Record<ConfiguratorSelectionType, string>

/** How a measure arrives at its price. */
export const PRICE_MODE_LABELS = {
  CALCULATED: TERMS.calculation,
  INCLUDED: COST_LABELS.priceIncluded,
  ON_REQUEST: COST_LABELS.priceOnRequest,
} as const satisfies Record<ConfiguratorPriceMode, string>

/** The three kinds of term that have no catalogue entry of their own. */
export const BLOCK_TYPE_LABELS = {
  FIXED_PRICE: CONFIGURATOR_CMS.priceFixed,
  FORMULA: TERMS.formula,
  MARK_UP: 'Aufschlag %',
} as const satisfies Record<CalculationBlockType, string>

/**
 * What stopped a measure from being added, said in the position list of the
 * project it was added to anyway.
 */
export const POSITION_GATE_LABELS = {
  NOT_AVAILABLE: 'Nicht als Position',
  HIDDEN: 'Ausgeblendet',
  DISABLED: 'Gesperrt',
} as const satisfies Record<PositionGate, string>

/**
 * What stopped a position from answering its question — said at the red mark
 * in the position list, and in the confirmation that produced it.
 *
 * Each names the change, not the consequence: the consequence is the same for
 * all four and the surface says it once.
 */
export const POSITION_ORPHAN_REASON_LABELS = {
  MEASURE_UNPLACED: `${TERMS.measure} nicht mehr in der ${TERMS.question}`,
  STRUCTURE_MOVED: `${TERMS.configurator}-Struktur geändert`,
  PLACE_RETYPED: `${TERMS.room} oder ${TERMS.floor} geändert`,
} as const satisfies Record<PositionOrphanReason, string>

/**
 * How an administrator moved a position off what the catalogue charges.
 *
 * Beside `POSITION_GATE_LABELS`, the other enum the same table carries.
 */
export const POSITION_PRICE_ADJUSTMENT_LABELS = {
  FIXED: 'Festpreis',
  DISCOUNT_PERCENT: 'Rabatt in Prozent',
  DISCOUNT_AMOUNT: 'Rabatt in Euro',
} as const satisfies Record<PositionPriceAdjustment, string>

/** What a rule's pictures do to the gallery the measure already has. The
 *  products' pictures follow in every case. */
export const IMAGE_RULE_MODE_LABELS = {
  PREPEND: 'Vorne anstellen',
  APPEND: 'Hinten anhängen',
  REPLACE: 'Ersetzen',
} as const satisfies Record<ConfiguratorImageRuleMode, string>

/** Which rule wins where a measure is both required and disabled. */
export const AVAILABILITY_CONFLICT_LABELS = {
  REQUIRED_WINS: 'Zwangsauswahl gewinnt',
  DISABLED_WINS: 'Deaktivierung gewinnt',
} as const satisfies Record<ConfiguratorAvailabilityConflict, string>

/**
 * How an energy class is written on screen. The schema spells the best class
 * `AA` because an identifier cannot start with a symbol; every surface prints
 * `A+`.
 *
 * Key order is the scale, best first: `ENERGY_LABELS` derives from it and
 * `EnergyEfficiencyIndicator` positions its markers by index, so resorting
 * this table moves the bar.
 */
export const ENERGY_LABEL_LABELS = {
  AA: 'A+',
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
  F: 'F',
  G: 'G',
  H: 'H',
} as const satisfies Record<EnergyLabel, string>

/** The schema's members in scale order, best first. */
export const ENERGY_LABEL_VALUES = Object.keys(
  ENERGY_LABEL_LABELS,
) as EnergyLabel[]

/**
 * Resolves a subtype as HubSpot's `property_type` picklist stores it — the
 * enum member, in whatever case — to its German label. A value the picklist
 * carries that is not an enum member passes through unchanged.
 */
export function propertySubTypeLabel(value: string): string {
  return PROPERTY_SUB_TYPE_LABELS[value.toUpperCase() as SubType] ?? value
}

/** Where a project stands in its lifecycle. */
export const PROJECT_STATUS_LABELS = {
  DRAFT: TERMS.draft,
  IN_PLANNING: 'In Planung',
  PUBLIC: 'Öffentlich',
  UNDER_CONSTRUCTION: 'Im Bau',
  DONE: 'Abgeschlossen',
  ARCHIVED: 'Archiviert',
} as const satisfies Record<ProjectStatus, string>

/**
 * What a term's amount is where a report breaks a price up.
 *
 * Read by the pane that sets it: the engine adds every role the same way, and
 * only the planning tells them apart.
 */
export const BLOCK_ROLE_LABELS = {
  COST: 'Kosten',
  MARGIN: COST_LABELS.margin,
  VAT: COST_LABELS.vat,
} as const satisfies Record<CalculationBlockRole, string>

/** The kinds of entry in the catalogue. Plural, because every surface that
 *  renders one heads a group of them. */
export const CATALOGUE_KIND_LABELS = {
  MEASURE: TERMS.measurePlural,
  PRODUCT: TERMS.productPlural,
  LABOUR: TERMS.labourItemPlural,
  FORMULA: TERMS.formulaCollection,
  PRICE_BLOCK: TERMS.blockPlural,
  PRICE_GROUP: TERMS.priceGroupPlural,
} as const satisfies Record<CatalogueKind, string>

/** The same in the singular, for an action that creates one. */
export const CATALOGUE_KIND_SINGULAR_LABELS = {
  MEASURE: TERMS.measure,
  PRODUCT: TERMS.product,
  LABOUR: TERMS.labourItem,
  FORMULA: TERMS.formula,
  PRICE_BLOCK: TERMS.block,
  PRICE_GROUP: TERMS.priceGroup,
} as const satisfies Record<CatalogueKind, string>

/** The four lists a product is filed under. Plural, because a surface that
 *  renders one heads a group of them. */
export const PRODUCT_CLASSIFICATION_KIND_LABELS = {
  CATEGORY: TERMS.productCategoryPlural,
  GROUP: TERMS.productGroupPlural,
  DESIGN_LINE: TERMS.designLinePlural,
  STANDARD: TERMS.productStandardPlural,
} as const satisfies Record<ProductClassificationKind, string>

/** What kind of value an axis holds. A member added to the enum without a word
 *  here fails the build. */
export const PRODUCT_PROPERTY_TYPE_LABELS = {
  NUMBER: 'Zahl',
  SELECT: 'Auswahl',
  BOOLEAN: `${COMMON.yes}/${COMMON.no}`,
} as const satisfies Record<ProductPropertyType, string>

/** Where a suggested product link stands. A confirmed one is a link and no
 *  longer a suggestion. */
export const PRODUCT_SUGGESTION_STATUS_LABELS = {
  OPEN: 'Offen',
  REJECTED: 'Abgelehnt',
} as const satisfies Record<ProductSuggestionStatus, string>

/** Whether a house element is priced by how many there are or by which option
 *  was picked. */
export const HOUSE_ELEMENT_MODE_LABELS = {
  QUANTITY: FIELD_LABELS.quantity.label,
  OPTION: TERMS.selection,
} as const satisfies Record<HouseElementMode, string>

/** Whether a house element belongs to the property or to one room. */
export const HOUSE_ELEMENT_SCOPE_LABELS = {
  GLOBAL: SCOPE_GLOBAL,
  PER_ROOM: 'Pro Raum',
} as const satisfies Record<HouseElementScope, string>

/** How a formula marked for the planning summary is evaluated. */
export const FORMULA_SUMMARY_SCOPE_LABELS = {
  GLOBAL: SCOPE_GLOBAL,
  PER_ROOM_SUM: 'Summe',
} as const satisfies Record<FormulaSummaryScope, string>

/** The figures the property states of itself, as the planning summary and the
 *  strip over Planning name them. */
export const PROPERTY_METRIC_LABELS = {
  PROPERTY_TYPE: FIELD_LABELS.type.short,
  // Every storey, whatever its type. „Ebene" and not Geschoss: the same
  // surfaces state Vollgeschosse, which is a different and smaller figure.
  LEVELS: 'Ebenen',
  FULL_FLOORS: FIELD_LABELS.numberOfFloors.label,
  // The rooms laid out on the plan — never the property's `numberOfRooms`,
  // which is what the listing claims.
  ROOMS: 'Räume',
  AREA: FIELD_LABELS.area.label,
} as const satisfies Record<PropertyMetric, string>

/** Where an upload link hangs the picture once it is there. */
export const MCP_UPLOAD_TARGET_LABELS = {
  LIBRARY: 'Nur Mediathek',
  MEASURE_GALLERY: `${TERMS.previewImagePlural} der Maßnahme`,
  MEASURE_CARD: 'Kartenbild der Maßnahme',
  PRODUCT_GALLERY: `${TERMS.previewImagePlural} des Produkts`,
  PRODUCT_CARD: 'Kartenbild des Produkts',
} as const satisfies Record<McpUploadTarget, string>

// ── Finances ────────────────────────────────────────────────────────────────

/** Which way the money moves. */
export const CONTRACT_DIRECTION_LABELS = {
  RECEIVABLE: 'Kundenvertrag',
  PAYABLE: `Auftrag an ${TERMS.subcontractor}`,
} as const satisfies Record<ContractDirection, string>

export const CONTRACT_KIND_LABELS = {
  MAIN: 'Hauptvertrag',
  SUPPLEMENT: FINANCE_TERMS.supplement,
  DIRECT: FINANCE_TERMS.withoutContract,
} as const satisfies Record<ContractKind, string>

export const CONTRACT_STATUS_LABELS = {
  DRAFT: TERMS.draft,
  ACTIVE: 'Beauftragt',
  CANCELLED: 'Aufgehoben',
} as const satisfies Record<ContractStatus, string>

/** When an installment falls due. */
export const INSTALLMENT_DUE_KIND_LABELS = {
  MILESTONE: `Mit ${FINANCE_TERMS.milestone}`,
  IMMEDIATE: 'Sofort',
  DATE: 'Zum Datum',
} as const satisfies Record<InstallmentDueKind, string>

/** What a deduction's percentage is taken of. */
export const DEDUCTION_BASIS_LABELS = {
  PER_INVOICE: 'Von jeder Rechnung',
  OF_CONTRACT: 'Von der Vertragssumme',
} as const satisfies Record<DeductionBasis, string>

/** What becomes of the withheld money. */
export const DEDUCTION_SETTLEMENT_LABELS = {
  RELEASED: 'Sicherheitseinbehalt',
  NEVER_PAID: 'Umlage',
} as const satisfies Record<DeductionSettlement, string>

/** What becomes of the money, under the choice of a deduction's kind. */
export const DEDUCTION_SETTLEMENT_HINTS = {
  RELEASED: 'Wird einbehalten und mit dem Meilenstein unten fällig.',
  NEVER_PAID: 'Wird abgezogen und entfällt dauerhaft.',
} as const satisfies Record<DeductionSettlement, string>

/** The bank's own state of a transaction. */
export const BANK_TRANSACTION_STATUS_LABELS = {
  PENDING: 'Vorgemerkt',
  COMPLETED: 'Gebucht',
  DECLINED: 'Abgelehnt',
  REVERSED: 'Storniert',
} as const satisfies Record<BankTransactionStatus, string>

export const INVOICE_STATUS_LABELS = {
  OPEN: FINANCE_FIGURES.issued,
  CANCELLED: 'Storniert',
} as const satisfies Record<InvoiceStatus, string>

/** How far an installment has got, as the tab and the customer's plan say it. */
export const INSTALLMENT_STATE_LABELS = {
  PLANNED: FINANCE_FIGURES.planned,
  DUE: FINANCE_FIGURES.due,
  INVOICED: FINANCE_FIGURES.issued,
  APPROVED: FINANCE_FIGURES.approved,
  PAID: FINANCE_FIGURES.paid,
} as const satisfies Record<InstallmentState, string>
